import cn from 'clsx'
import s from './Hero.module.scss'
import { FC, useRef, useEffect, useState } from 'react'
import {
  Text,
  CustomImage,
  Container,
  Button,
  VideoPlayer,
} from '@components/ui'
import { isMobileWidth } from '@utils/helpers'
import { useWindowSize } from '@hooks/useWindowSize'
import Image from 'next/image'
import { LoreThreePage } from '@generated/model/loreThreePage'
import Link from 'next/link'
import { BigPlayButton, ControlBar, Player, PlayerReference } from 'video-react'
import { useIsomorphicLayoutEffect } from 'usehooks-ts'

type HeroProps = {
  page: LoreThreePage
}

const Hero: FC<HeroProps> = ({ page }) => {
  const playerRef = useRef<PlayerReference>(null)

  useEffect(() => {
    const player = playerRef.current
    if (player) {
      const handleEnd = () => {
        player.seek(0)
        player.play()
      }

      player.subscribeToStateChange((state) => {
        if (state.ended) {
          handleEnd()
        }
      })
    }
  }, [])

  if (!page.hero || !page.table) return null

  return (
    <>
      <section className={s.root}>
        <Container>
          {/* <Image
            className={s.background}
            src="/background-gradient.svg"
            layout="responsive"
            width={390}
            height={166}
          /> */}

          {page.hero.video || page.hero.video_url ? (
            <VideoPlayer
              data={page.hero}
              controls={page.hero.controls}
              className={s.videoContainer}
            />
          ) : (
            <div className={s.videoContainer}>
              <Image
                src={page.hero.image as unknown as string}
                alt={page.hero.alt || ''}
                width={page.hero.image_width || 1920}
                height={page.hero.image_height || 1080}
                layout="responsive"
                priority
              />
            </div>
          )}

          <div className={s.inner}>
            <Text
              html={page.hero.title}
              className={s.title}
              variant="pageHeading"
            />
            <Link href={page.hero.cta_link || '/'}>
              <a className={s.button}>
                <Button Component="span" className={s.buttonInner}>
                  Order Now
                </Button>
              </a>
            </Link>

            <div className={s.image}>
              <CustomImage data={page.table} priority />
            </div>
            {page.hero.detail && (
              <Text
                html={page.hero.detail}
                className={cn(s.detail)}
                variant="sectionHeadingV2"
              />
            )}
          </div>

          <div className={s.grid}>
            <div className={s.col}>
              <div className={s.icon}>
                <div className={s.iconInner}>
                  <Image
                    src={page.table.column_1_icon as string}
                    alt={s.title}
                    width={50}
                    height={50}
                    layout="responsive"
                  />
                </div>
              </div>
              <div className={s.mobileColumn}>
                <div className={s.titleContainer}>
                  <Text
                    variant="contentHeading"
                    html={page.table.column_1_title}
                    className={s.tableTitle}
                  />
                </div>
                <Text html={page.table.column_1_detail} className={s.detail} />
              </div>
            </div>
            <div className={s.col}>
              <div className={s.icon}>
                <div className={s.iconInner}>
                  <Image
                    src={page.table.column_2_icon as string}
                    alt={s.title}
                    width={50}
                    height={50}
                    layout="responsive"
                  />
                </div>
              </div>
              <div className={s.mobileColumn}>
                <div className={s.titleContainer}>
                  <Text
                    variant="contentHeading"
                    html={page.table.column_2_title}
                    className={s.tableTitle}
                  />
                </div>
                <Text html={page.table.column_2_detail} className={s.detail} />
              </div>
            </div>
            <div className={s.col}>
              <div className={s.icon}>
                <div className={s.iconInner}>
                  <Image
                    src={page.table.column_3_icon as string}
                    alt={s.title}
                    width={50}
                    height={50}
                    layout="responsive"
                  />
                </div>
              </div>
              <div className={s.mobileColumn}>
                <div className={s.titleContainer}>
                  <Text
                    variant="contentHeading"
                    html={page.table.column_3_title}
                    className={s.tableTitle}
                  />
                </div>
                <Text html={page.table.column_3_detail} className={s.detail} />
              </div>
            </div>

            <div className={s.tableDetail}>
              <Text className={s.detail} html={page.table.table_detail} />
            </div>
          </div>
        </Container>
      </section>
    </>
  )
}

export default Hero
